import {getProduct} from "../data/products/index";

/**
 * The code highlight of a banner: a rounded rectangle over a screenshot that
 * marks a part of the code (Figma "YouTube", node 12710:1552
 * "overlay/code-highlight"). It is a layer above the screenshot, not a part
 * of the image.
 *
 * The scene record `codeHighlights` keeps one per banner id. The box is in
 * banner pixels. The user moves the box and changes its width and height
 * independently. The outline and the corner radius stay the same at every
 * size: only the box changes.
 */
export interface CodeHighlight {
  enabled: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  /** "#rrggbb": the outline in this colour, the fill in this colour at 20 %. */
  color: string;
}

export type CodeHighlightBox = Pick<CodeHighlight, "x" | "y" | "width" | "height">;

/** The outline is outside the box, so the box keeps the size of the code it marks. */
export const CODE_HIGHLIGHT_OUTLINE_PX = 4;
export const CODE_HIGHLIGHT_RADIUS_PX = 10;
export const CODE_HIGHLIGHT_FILL_OPACITY = 0.2;
/** The smallest width and height a resize gives, in banner pixels. */
export const CODE_HIGHLIGHT_MIN_PX = 12;

/**
 * The one-line box of the design, on a 1920x1080 banner. It waits at the
 * bottom, clear of the drop zone label in the centre: the space below the box
 * is the same as the space to its left (136 px).
 */
export const DEFAULT_CODE_HIGHLIGHT_BOX: Readonly<CodeHighlightBox> = Object.freeze({
  x: 136,
  y: 895,
  width: 780,
  height: 49,
});

/**
 * The colour of the reference design (IntelliJ IDEA, Layout Guides). A
 * product without its own colour in the design gets it.
 */
export const DEFAULT_CODE_HIGHLIGHT_COLOR = "#087CFA";

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR.test(value);
}

/** The highlight colour of a product: `codeHighlightColor` in its config (src/data/products). */
export function productCodeHighlightColor(productCode: string): string {
  const color = (getProduct(productCode) as {codeHighlightColor?: unknown} | undefined)
    ?.codeHighlightColor;
  if (color === undefined) return DEFAULT_CODE_HIGHLIGHT_COLOR;
  if (!isHexColor(color)) {
    throw new Error(`Product ${productCode}: codeHighlightColor must be "#rrggbb", got ${color}`);
  }
  return color;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** True for a complete, valid highlight. */
export function isCodeHighlight(value: unknown): value is CodeHighlight {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.enabled === "boolean" &&
    isFiniteNumber(v.x) &&
    isFiniteNumber(v.y) &&
    isFiniteNumber(v.width) &&
    isFiniteNumber(v.height) &&
    v.width > 0 &&
    v.height > 0 &&
    isHexColor(v.color)
  );
}

/**
 * The record of a loaded scene, with only valid entries. A scene saved
 * before the field has none.
 */
export function normalizeCodeHighlights(value: unknown): Record<string, CodeHighlight> {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(([, entry]) => isCodeHighlight(entry))
  ) as Record<string, CodeHighlight>;
}

/** The highlight of one banner: its saved value, or the design default in the product's colour. */
export function codeHighlightOf(
  record: Record<string, CodeHighlight> | undefined,
  id: string,
  productCode: string
): CodeHighlight {
  const saved = record?.[id];
  if (saved) return saved;
  return {enabled: true, ...DEFAULT_CODE_HIGHLIGHT_BOX, color: productCodeHighlightColor(productCode)};
}

/** The fill: the colour at 20 % opacity. */
export function codeHighlightFill(color: string): string {
  if (!isHexColor(color)) throw new Error(`Code highlight colour must be "#rrggbb", got ${color}`);
  const alpha = Math.round(CODE_HIGHLIGHT_FILL_OPACITY * 255)
    .toString(16)
    .padStart(2, "0");
  return `${color}${alpha}`;
}

/** The edges a resize handle moves. */
export type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export const RESIZE_HANDLES: readonly ResizeHandle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** The box moved by (dx, dy), kept inside the banner. */
export function moveCodeHighlightBox(
  box: CodeHighlightBox,
  dx: number,
  dy: number,
  canvas: {width: number; height: number}
): CodeHighlightBox {
  return {
    ...box,
    x: Math.round(clamp(box.x + dx, 0, Math.max(0, canvas.width - box.width))),
    y: Math.round(clamp(box.y + dy, 0, Math.max(0, canvas.height - box.height))),
  };
}

/**
 * The box with the edges of `handle` moved by (dx, dy). The opposite edges
 * stay. Width and height change independently, are at least
 * CODE_HIGHLIGHT_MIN_PX, and the box stays inside the banner.
 */
export function resizeCodeHighlightBox(
  box: CodeHighlightBox,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  canvas: {width: number; height: number}
): CodeHighlightBox {
  let left = box.x;
  let top = box.y;
  let right = box.x + box.width;
  let bottom = box.y + box.height;
  if (handle.includes("w")) left = clamp(left + dx, 0, right - CODE_HIGHLIGHT_MIN_PX);
  if (handle.includes("e")) right = clamp(right + dx, left + CODE_HIGHLIGHT_MIN_PX, canvas.width);
  if (handle.includes("n")) top = clamp(top + dy, 0, bottom - CODE_HIGHLIGHT_MIN_PX);
  if (handle.includes("s")) bottom = clamp(bottom + dy, top + CODE_HIGHLIGHT_MIN_PX, canvas.height);
  return {
    x: Math.round(left),
    y: Math.round(top),
    width: Math.round(right - left),
    height: Math.round(bottom - top),
  };
}
